import { getKeys } from '@jack-henry/frontend-utils/functions';
import { DeepPartial, Nullable } from '@jack-henry/frontend-utils/types';
import {
    TmDialogButton,
    TmDialogButtonMap,
    TmDialogButtonPosture,
    TmDialogOrientation,
    type TmDialogServiceConfig,
} from '@treasury/presentation/services/dialog';
import { FlexDirection } from '@treasury/utils/types';
import {
    DialogButtonPosture,
    OmegaDialogButtonMap,
    OmegaDialogButtonsOptional,
    OmegaDialogOptions,
} from './omega-dialog.service.types';

/**
 * Maps the shape of the dialog service's public API configuration
 * to the shape used by `<omega-dialog>`.
 */
export function mapConfig<T>(config: TmDialogServiceConfig<T>): OmegaDialogOptions {
    const { preventClose, showButtons, buttons, orientation, host } = config;

    return {
        host,
        preventClose,
        flexDirection: mapOrientation(orientation),
        renderButtons: showButtons,
        buttons: mapButtons(buttons),
    };
}

function mapOrientation(orientation?: TmDialogOrientation): FlexDirection | undefined {
    if (!orientation) {
        return undefined;
    }

    switch (orientation) {
        case 'horizontal':
            return 'row';
        case 'vertical':
            return 'column';
        default:
            return undefined;
    }
}

function mapButtons<T>(
    buttons: TmDialogServiceConfig<T>['buttons']
): OmegaDialogButtonsOptional | undefined {
    if (!buttons) {
        return undefined;
    }

    return Array.isArray(buttons) ? mapButtonArray(buttons) : mapButtonDict(buttons);
}

function mapButtonDict(buttons: DeepPartial<Nullable<TmDialogButtonMap>>) {
    return getKeys(buttons).reduce((map, posture) => {
        const button = buttons[posture];

        if (button && button.reason) {
            const { reason, label } = button;
            map[reason] = {
                label,
                posture: mapPosture(posture),
            };
        }

        return map;
    }, {} as OmegaDialogButtonsOptional);
}

function mapButtonArray<T>(buttons: Partial<TmDialogButton<T>>[]) {
    return buttons.reduce((map, button) => {
        const { label, reason, onClick: originalOnClick, posture } = button;
        /**
         * No-op that throws since `<omega-dialog>` doesn't support the same behavior as `<tm-dialog>`.
         */
        const closeFn = () => {
            throw new Error(
                'Could not close dialog from within a button onClick(). Omega dialog does not support this.'
            );
        };

        if (!reason || !label) {
            throw new Error(
                'Could not map TmDialogButton to Omega button. A label and/or a reason was not provided.'
            );
        }

        map[reason] = {
            label,
            posture: mapPosture(posture),
            onClick: originalOnClick ? () => originalOnClick(closeFn) : undefined,
        };

        return map;
    }, {} as OmegaDialogButtonsOptional);
}

function mapPosture(posture?: TmDialogButtonPosture): DialogButtonPosture {
    if (!posture) {
        return DialogButtonPosture.Primary;
    }

    switch (posture) {
        case TmDialogButtonPosture.Primary:
            return DialogButtonPosture.Primary;
        case TmDialogButtonPosture.Secondary:
            return DialogButtonPosture.Secondary;
        default:
            throw new Error(`Could not map button posture for value: ${posture}`);
    }
}
